import Cookies from "js-cookie";

const ACCESS_TOKEN_COOKIE = "jobmein_access_token";
const REFRESH_TOKEN_COOKIE = "jobmein_refresh_token";
const USER_COOKIE = "jobmein_user";

const isSecureContext =
  typeof window !== "undefined"
    ? window.location.protocol === "https:"
    : true;

const buildCookieOptions = (rememberMe?: boolean): Cookies.CookieAttributes => {
  const options: Cookies.CookieAttributes = {
    path: "/",
    sameSite: "lax",
    secure: isSecureContext,
  };

  if (rememberMe) {
    options.expires = 30;
  }

  return options;
};

export const saveTokens = (
  access: string,
  refresh: string,
  rememberMe?: boolean,
  user?: { 
    id: number; 
    name: string; 
    email: string;
    is_active?: boolean;
    pwd_reset_required?: boolean;
    group?: string;
    permissions?: unknown[];
    company_member_id?: number;
    [key: string]: unknown;
  }
) => {
  const options = buildCookieOptions(rememberMe);
  Cookies.set(ACCESS_TOKEN_COOKIE, access, options);
  Cookies.set(REFRESH_TOKEN_COOKIE, refresh, options);
  if (user) {
    Cookies.set(USER_COOKIE, JSON.stringify(user), options);
  }
};

export const clearTokens = () => {
  const cookieNames = [ACCESS_TOKEN_COOKIE, REFRESH_TOKEN_COOKIE, USER_COOKIE];

  cookieNames.forEach(name => {
    Cookies.remove(name, { path: "/" });
    Cookies.remove(name, { path: "/", domain: window.location.hostname });
    if (window.location.hostname.includes('.')) {
      Cookies.remove(name, { path: "/", domain: `.${window.location.hostname}` });
    }
  });

  // Method 2: Direct document.cookie with path=/
  if (typeof document !== "undefined") {
    const expires = "expires=Thu, 01 Jan 1970 00:00:00 UTC";

    cookieNames.forEach(name => {
      // Standard removal
      document.cookie = `${name}=; ${expires}; path=/`;

      // With domain variations
      document.cookie = `${name}=; ${expires}; path=/; domain=${window.location.hostname}`;

      if (window.location.hostname.includes('.')) {
        const rootDomain = window.location.hostname.split('.').slice(-2).join('.');
        document.cookie = `${name}=; ${expires}; path=/; domain=.${window.location.hostname}`;
        document.cookie = `${name}=; ${expires}; path=/; domain=${rootDomain}`;
        document.cookie = `${name}=; ${expires}; path=/; domain=.${rootDomain}`;
      }

      // With SameSite variations
      document.cookie = `${name}=; ${expires}; path=/; SameSite=Lax`;
      document.cookie = `${name}=; ${expires}; path=/; SameSite=Strict`;
      document.cookie = `${name}=; ${expires}; path=/; SameSite=None; Secure`;

      // Using max-age instead of expires
      document.cookie = `${name}=; max-age=0; path=/`;
      document.cookie = `${name}=; max-age=-1; path=/`;
    });
  }

  // Method 3: Overwrite with empty string
  cookieNames.forEach(name => {
    Cookies.set(name, "", {
      path: "/",
      expires: new Date(0),
    });
  });

  setTimeout(() => {
    // Check if cookies are httpOnly (can't be deleted from JS)
    if (document.cookie.includes(ACCESS_TOKEN_COOKIE)) {
      console.warn("⚠️ Cookies still present! They may be httpOnly (set by backend)");
      console.warn("You need to add a backend logout endpoint to clear httpOnly cookies");
    } else {
      console.log("✅ Cookies cleared successfully!");
    }
  }, 100);
};

export const getAccessToken = () => Cookies.get(ACCESS_TOKEN_COOKIE) ?? null;
export const getRefreshToken = () => Cookies.get(REFRESH_TOKEN_COOKIE) ?? null;
export const getUserCookie = () => {
  const user = Cookies.get(USER_COOKIE);
  try {
    return user ? JSON.parse(user) : null;
  } catch {
    return null;
  }
};
export const hasStoredTokens = () =>
  Boolean(getAccessToken() && getRefreshToken());